import React from 'react';
import { useParams } from 'react-router-dom';

import useGoalsMonitoring from 'containers/user/goals/goals-monitoring/monitoring-chart/hooks/use-goals-monitoring';
import CriticalElementChart from 'containers/user/goals/goals-monitoring/monitoring-chart/critical-element-chart';
import Button from 'components/partials/button/button';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';
import Heading from 'components/partials/heading/heading';
import Loader from 'components/partials/loader/loader';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';

import { captureInsightScreenshot } from 'helpers/utils/insight-screenshot';
import { MONITORING_CHART_MAX_WIDTH } from 'containers/user/goals/goals-monitoring/monitoring-chart/hooks/constants';

import Icons from 'assets/icons';

interface MetaDataProps {
  label: string;
  value?: React.ReactNode;
}

const renderMetaData = ({
  label,
  value = 'N/A',
}: MetaDataProps): React.JSX.Element => (
  <div className="flex items-center gap-x-2">
    <span className="text-sm text-gray-600">{`${label}:`}</span>
    <span className="text-sm text-gray-600 font-semibold">{value}</span>
  </div>
);

const AdminGoalsMonitoring = (): React.JSX.Element => {
  const {
    clientId,
    year: enteringClassYear,
    goalType,
    goalCategory,
    goalId,
  } = useParams<{
    clientId: string;
    year: string;
    goalType: string;
    goalCategory: string;
    goalId: string;
  }>();

  const { loading, monitoringSettings, displayMetrics, goalData } =
    useGoalsMonitoring(goalId, clientId);

  const breadcrumbs = [
    { label: 'Clients', href: '/admin/clients' },
    {
      label: 'Goals',
      href: `/admin/clients/${clientId}/goals/${enteringClassYear}`,
    },
    {
      label: 'Goal details',
      href: `/admin/clients/${clientId}/goals/${enteringClassYear}/${goalType}/${goalCategory}/${goalId}`,
    },
  ];

  return (
    <ResponsiveWrapper customClass="h-full">
      <Loader loading={loading} fullScreen={false} />
      <Breadcrumbs breadcrumbs={breadcrumbs} customClass="mt-7 mb-3" />

      <Heading variant="h1" customClass="mb-2 block text-2xl">
        {goalData?.summary || `Goal ${goalId}`}
      </Heading>

      <div className="flex items-center gap-x-4 mb-5">
        {renderMetaData({
          label: 'Cohort',
          value: monitoringSettings?.cohort,
        })}
        {renderMetaData({
          label: 'Entry term',
          value: goalData?.enteringClassYear,
        })}
      </div>
      <div className="flex flex-wrap gap-8 items-end">
        {!!displayMetrics.length &&
          displayMetrics.map((ce) => (
            <div
              key={ce.criticalElementId}
              style={{ maxWidth: MONITORING_CHART_MAX_WIDTH }}
              className="mb-6"
            >
              <div className="flex justify-between items-center mb-2">
                <div className="font-bold text-gray-700">
                  {ce.criticalElementSummary}
                </div>
                <Button
                  variant="reserve-outlined"
                  size="sm"
                  className="shrink-0 ml-4"
                  iconBefore={<Icons.Insight aria-label="InsightIcon" />}
                  onClick={() =>
                    captureInsightScreenshot(
                      document.getElementById(`chart-${ce.criticalElementId}`),
                      {
                        title: '',
                        description: '',
                        summary: ce.criticalElementSummary,
                        criticalElement: ce.criticalElementSummary,
                        criticalElementId: ce.criticalElementId,
                        clientId,
                        relatedGoals: [],
                        source: 'monitoring',
                      } as never
                    )
                  }
                >
                  <span>Add insight</span>
                </Button>
              </div>
              <CriticalElementChart
                chartId={`chart-${ce.criticalElementId}`}
                blocks={ce.blocks}
                previousYear={
                  monitoringSettings?.comparisonYears?.[0] ?? 'Prev'
                }
                currentYear={monitoringSettings?.comparisonYears?.[1] ?? 'Curr'}
                cohortName={monitoringSettings?.cohort}
                isRestricted={ce.isRestricted}
              />
            </div>
          ))}
      </div>
    </ResponsiveWrapper>
  );
};

export default AdminGoalsMonitoring;
